import {useCallback, useState} from 'react';
/**
 * 🔘 useSetStateAsBoolean
 *
 * A simple custom React hook for managing boolean state.
 * Provides helper functions to set the value to `true`, `false`, or toggle it.
 *
 * @param {boolean} [initialValue=false] - The initial boolean value.
 * @returns {{
 *   boolVal: boolean,       // Current boolean state
 *   setTrue: () => void,    // Sets the state to true
 *   setFalse: () => void,   // Sets the state to false
 *   toggle: () => void      // Toggles the state between true/false
 * }}
 *
 * @example
 * const { boolVal, setTrue, setFalse, toggle } = useSetStateAsBoolean();
 *
 * <Button onClick={toggle}>
 *   {boolVal ? "ON" : "OFF"}
 * </Button>
 */

function useSetStateAsBoolean(initialValue: boolean = false) {
  const [boolVal, setBoolVal] = useState(initialValue);

  const setTrue = useCallback(() => setBoolVal(true), []);
  const setFalse = useCallback(() => setBoolVal(false), []);
  const toggle = useCallback(() => setBoolVal(v => !v), []);

  return {boolVal, setTrue, setFalse, toggle};
}

export default useSetStateAsBoolean;
